<template>
  <label class="ui-textbox">
    <template v-if="title">
      <div class="ui-textbox__title">
        {{ title }}
      </div>
    </template>

    <error-tooltip
      :error="error"
      :z-index="zIndexTooltip"
      class="ui-textbox__error" />

    <div class="ui-textbox__inner">
      <textarea
        ref="inner"
        :value="val"
        :style="textAreaStyles"
        :disabled="disabled"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :readonly="readonly"
        class="ui-textbox__text"
        @focus="onFocus"
        @keypress="onKeyPress"
        @paste="onPaste"
        @input="setValue" />

      <div
        v-if="isOverflowed"
        class="ui-textbox__shadow" />

      <p
        v-if="maxlength"
        class="ui-textbox__length">
        {{ val.length }} / {{ maxlength }}
      </p>
    </div>
  </label>
</template>

<script>
import ErrorTooltip from 'Components/ui/Tooltip/ErrorTooltip.vue';

export default {
  name: 'UiTextbox',

  components: {
    ErrorTooltip,
  },

  props: {
    value: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    resize: {
      type: String,
      default: 'none',
    },

    error: {
      type: String,
      default: null,
    },

    title: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    zIndexTooltip: {
      type: Number,
      default: 100,
    },

    regexpFilter: {
      type: [RegExp, Boolean],
      default: false,
    },

    maxlength: {
      type: [String, Number],
      default: null,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    elastic: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      val: this.value,
      isOverflowed: false,
    };
  },

  computed: {
    textAreaStyles() {
      return {
        resize: this.resize,
      };
    },
  },

  watch: {
    value: {
      handler(newVal) {
        if (this.val !== newVal) {
          this.val = newVal || '';
        }
      },
    },
  },

  mounted() {
    this.checkOverflow();
    this.setHeight();
  },

  methods: {
    setValue(event) {
      this.val = event.target.value;
      this.$emit('input', this.val);
      this.checkOverflow();
      this.setHeight();
    },

    onFocus(event) {
      this.$emit('focus', event);
    },

    checkOverflow() {
      if (!this.elastic) {
        this.isOverflowed = this.$refs.inner.scrollHeight > this.$refs.inner.clientHeight;
      }
    },

    onKeyPress(event) {
      if (event.key !== this.sanitizeValue(event.key)) {
        event.preventDefault();
      }
    },

    onPaste(event) {
      setTimeout(() => {
        const filtered = this.sanitizeValue(event.target.value);
        if (event.target.value !== filtered) {
          this.val = filtered;
        }
      }, 0);
    },

    sanitizeValue(value) {
      let filtered = value;

      if (this.regexpFilter) {
        filtered = filtered.replace(this.regexpFilter, '');
      }

      return filtered;
    },

    setHeight() {
      if (this.elastic) {
        this.$refs.inner.style.height = '5px';
        this.$refs.inner.style.height = `${this.$refs.inner.scrollHeight}px`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .ui-textbox {
    --padding: 20px;
    --background-color: #{$color--additional-menu-hsl};
    --background-color--focused: #{$color--basic-superdark-gray-hsl};
    --color: #{$color--basic-white};
    --placeholder-color: #{$color--basic-light-gray};
    --font-weight: 300;
    --font-size: #{$text-13px};
    --border-radius: 3px;
    --height: 100%;
    --title-font-weight: var(--font-weight);
    --min-height: initial;

    background-color: hsl(var(--background-color));
    position: relative;
    width: 100%;
    height: var(--height);
    border-radius: var(--border-radius);
    font-size: 0;
    padding: var(--padding);
    display: flex;
    flex-direction: column;
    transition: background-color .25s ease-out;

    /* https://caniuse.com/#search=focus-within */
    &:focus-within {
      --background-color: var(--background-color--focused);
    }
  }

  .ui-textbox__text {
    --scroll-track-color: transparent;
    --scroll-thumb-color: #{rgba($color--basic-light-gray, .31)};

    color: var(--color);
    font-size: var(--font-size);
    font-family: 'Rubik', sans-serif;
    font-weight: var(--font-weight);
    min-height: var(--min-height);
    line-height: 1.31;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    outline: none;
    background-color: inherit;
    border: none;
    transition: background-color .2s ease-out;
    padding-right: 16px;

    &::placeholder {
      color: var(--placeholder-color);
      opacity: 1;
      font-family: 'Rubik', sans-serif;
      font-size: var(--font-size);
      font-weight: var(--title-font-weight);
    }
  }

  .ui-textbox__inner {
    position: relative;
    flex: 1 1 0;
  }

  .ui-textbox__error {
    position: absolute;
    right: 5px;
    top: 5px;
    z-index: 1;
  }

  .ui-textbox__shadow {
    position: absolute;
    width: calc(100% - 8px);
    height: 30px;
    left: 0;
    bottom: 0;
    background: linear-gradient(hsla(var(--background-color), 0) 0%, hsl(var(--background-color)) 100%);
    pointer-events: none;
  }

  .ui-textbox__title {
    font-size: var(--font-size);
    font-weight: var(--title-font-weight);
    transition: .2s ease-out;
    transition-property: color, transform, font-size;
    color: $color--basic-light-gray;
    margin-bottom: 10px;
    line-height: 1.31;
  }

  .ui-textbox__length {
    position: absolute;
    bottom: -4px;
    right: 0;
    font-size: 11px;
    font-weight: 500;
    transform: translateY(100%);
    color: $color--basic-light-gray;
  }
</style>
